<template>
    <div class="chat-box chat" v-if="room">

        <!-- PROFILE -->
        <chat-profile class="chat-box__header" v-if="user" @expand="profileExpanded" @cancel="$emit('cancel')" :forceRollUp="profileRolledUpByMessage" :user="user" />

        <div v-else class="chat-box__header chat-box-header">
            <div class="chat-box-header__top">
                <back-button class="chat-box-header__back" @click.native="$emit('cancel')">{{ $t("common.back") }}</back-button>

                <div class="chat-box-header__title" @click="openProfile" :class="{'chat-box-header__title_clickable': user }">
                    <span>{{ title }}</span>
                </div>
                <div class="chat-box-header__flex-box-stub"></div>
            </div>
        </div>

        <!-- body -->
        <messages :user="user" @sendContacts="sendContacts"
                  :room="room"
                  @resend="resend"
                  @fetchingEnded="onFetchingEnded"
                  @fetchingStarted="onFetchingStarted" ref="body"/>

        <div class="chat-box-footer" :class="{'chat-box-footer_with-profile' : user }" v-if="showFooter">
            <!-- ACTIONS: -->
            <div v-if="(isMeetingsEnabled && showMeetingsButtonToCurrentUser) || allowContactsShare" class="chat-box-footer__actions">
                <div class="chat-box-footer__buttons">
                    <button v-if="isMeetingsEnabled && (showMeetingsButtonToCurrentUser || showPayButtonToCurrentUser)"
                        @click="meetingHelper.createPublic(publicMeetingParams)"
                        :class="{ 'is-busy': meetingHelper.isBusy }"
                        class="chat-box-footer__meeting button is-green is-middle" type="button">
                        {{ $t('meetings.meeting') }}
                        <meeting-icon v-if="!meetingHelper.isBusy" class="chat-box-footer__meeting-icon"/>
                        <spinner v-else class="chat-box-footer__meeting-spinner"/>
                    </button>
                    <button v-if="allowContactsShare" @click="sendContacts"
                        class="chat-box-footer__send-contacts button is-light-grey is-middle">
                        {{ $t('common.send contacts')}}
                        <send-contacts-icon />
                    </button>
                </div>
            </div>
            <resizable-textarea>
                <textarea rows="1" id="chat-input" ref="input" type="text" @keydown="keydown" v-model="message" :placeholder="$t('common.message')"></textarea>
            </resizable-textarea>
            <button class="chat-box-footer__send" :class="{ 'chat-box-footer__send_active': message }" @click="send">
                <send></send>
            </button>
        </div>
        <button class="button is-green chat-box-footer btn-footer" v-if="showAttachFooter" @click="attach">
            {{ $t('common.connect') }}
        </button>
    </div>
</template>

<script>
    import Vue from 'vue';
    import Send from "../svg/Send";
    import Message from "./Message";
    import ChatTypes from "../../enums/ChatTypes";
    import MessageModel from '../../models/Messages/Message';
    import {DateTime} from 'luxon';
    import uuid from "../../helpers/uuid";
    import resizableTextarea from "../resizableTextarea";
    import Spinner from "../Spinner";
    import SendingStatuses from "../../enums/SendingStatuses";
    import PlainMessage from "./PlainMessage";
    import {EventBus as GlobalEventBus} from "@common/EventBus";
    import {ArrayHelpers} from "../../../../common/ArrayHelpers";
    import { ContactsShareHelper } from "../../helpers/ContactsShareHelper";
    import { PaymentAccess } from "../../../../mixins/PaymentAccess";
    import BackButton from "../../../../service/components/common/back-button/BackButton.vue";
    import ProfileButton from "../buttons/ProfileButton.vue";
    import MeetingIcon from "../svg/MeetingIcon.vue";
    import SendContactsIcon from "../svg/SendContacts.vue";

    import ChatProfile from "../chat-profile/ChatProfile.vue"
    import { UserProfile } from "@api/models/UserProfile";
    import { MeetingHelper } from "@share/chat/helpers/MeetingHelper";
    import { RepositoryFabric } from "@api/repositories/RepositoryFabric";
    import Messages from "@share/chat/components/chat/Messages";

    export default Vue.extend({
        name: "Chat",
        mixins: [PaymentAccess],
        components: {
            Messages,
            ChatProfile, Send, Message, resizableTextarea, Spinner, PlainMessage, BackButton, ProfileButton, MeetingIcon, SendContactsIcon},
        data() {
          return {
              message: '',
              profileRolledUpByMessage: false,
              meetingHelper: new MeetingHelper(this.$repositories.get(RepositoryFabric.MEETING), this)
          }
        },
        computed: {
            room() {
                return this.$store.state.chat.room.instance;
            },
            currentUserId() {
              return this.$store.state.session.userId;
            },
            user() {
                if(this.room && this.room.userId) {
                    const user = this.$store.state.chat.profiles.list[this.room.userId];
                    return user ? new UserProfile(user) : nul
                }
                else {
                    return null;
                }
            },
            title() {
                if (this.user) {
                    return this.user.fullName;
                }
                return this.room && this.room.title;
            },
            isChannel() {
                return this.room && this.room.type === ChatTypes.channel;
            },
            isMember() {
                return this.room.isMember;
            },
            showFooter() {
                if (this.isChannel && this.room.ownerId == this.$store.state.session.userId) {
                    return true;
                }
                return !this.isChannel && this.isMember;
            },
            showAttachFooter() {
                return !this.isMember && this.room.type != ChatTypes.direct;
            },
            lang() {
                return this.$store.state.session.language || 'en'
            },
            isMeetingsEnabled() {
                if (!this.$store.getters['events/allowMeetingWithSpeakers'] && this.isSpeaker) {
                    return false;
                }
                const params = this.$store.getters['events/selectedEventViewParam']('meetings', null);
                return this.user && params?.enabled !== false;
            },
            showMeetingsButtonToCurrentUser() {
                const userGroupIds = this.$store.getters['groupIds'];
                const params = this.$store.getters['events/selectedEventViewParam']('meetings', null);

                if (params?.groupIds) {
                    return !!ArrayHelpers.intersect(userGroupIds, params?.groupIds).length;
                }

                return true;
            },
            showPayButtonToCurrentUser() {
                const payParams = this.$store.getters['events/selectedEventViewParam']('accessPayments', null);
                return !this.showMeetingsButtonToCurrentUser && payParams !== null;
            },
            allowContactsShare() {
                return this.user !== null && this.room.type == ChatTypes.direct
            },
            isSpeaker() {
                if (this.user && this.$store.getters['events/selectedEvent']) {
                    return this.$store.getters['events/selectedEventSpeakers'].includes(this.user.userId);
                }
                return false;
            },
            publicMeetingParams() {
                return {
                    userIds: [this.room.userId],
                    eventId: this.$store.state.events.selected
                }
            }
        },
        watch: {
            room(newRoom, oldRoom) {
                //@todo move to messages component
                if ((newRoom && oldRoom) && newRoom.id != oldRoom.id) {
                   this.$refs['body'].fetchMessages();
                }
            },
            message() {
                if(!this.profileRolledUpByMessage) {
                    this.profileRolledUpByMessage = true;
                }
            }
        },
        methods: {
            profileExpanded() {
                this.profileRolledUpByMessage = false;
            },
            keydown(e) {
                if (e.keyCode == 13 && !e.shiftKey) {
                   e.preventDefault();
                   this.send();
                }
                else if (e.keyCode != 13) {
                    //this.typing();
                }
            },
            send() {
                this.prepareMessageAndSend(this.message);
            },
            prepareMessageAndSend(textMessage, attachments) {
                if (textMessage && textMessage.length) {
                    const newUuid = uuid();
                    const message = new MessageModel({
                        id: newUuid,
                        roomID: this.room.id,
                        message: textMessage,
                        attachments: attachments ? attachments : undefined,
                        type: 1,
                        localID: newUuid,
                        createdAt: DateTime.local(),
                        userID: this.$store.getters['chat/userId'],
                        status: SendingStatuses.sending
                    });

                    this.$store.dispatch('chat/room/addMessage', message);
                    this.$store.state.chat.socket.sendMessages(message.socketData());
                    this.message = '';
                    this.$nextTick(() => {
                        const event = new Event('input', {
                            bubbles: true,
                            cancelable: true,
                        });
                        this.$refs['input'].dispatchEvent(event);
                        this.$refs['body']?.scrollDown();
                    });
                }
            },
            async sendContacts() {

                const helper = new ContactsShareHelper(this.$store);

                if(helper.validateProfile()) {
                    const attachments = helper.getAttachments();
                    const fallBackMessageText = helper.getFallbackMessage();

                    this.prepareMessageAndSend(fallBackMessageText, attachments);
                }
                else {

                    const settingsShouldBeOpen = await helper.requestToCompleteProfile();

                    if(settingsShouldBeOpen) {
                        this.$router.push({
                            ...this.$route,
                            hash: "#settings"
                        })
                    }
                }
            },
            resend(message) {
                this.$store.dispatch('chat/room/changeMessage', {messageId: message.id, newValues: {status: SendingStatuses.sending}});
                this.$store.dispatch('chat/room/startTimer', message);
                // NOTE: возможно необходимо сделать reconnect
                this.$store.state.chat.socket.sendMessages(message.socketData());
            },
            back() {
                this.$emit('back');
            },
            attach() {
                this.$store.dispatch('chat/rooms/addUsersToChat', {
                    roomId: this.room.id,
                    users: [this.$store.state.session.userId]
                })
                .then(() => {
                    this.$nextTick(() => {
                        this.$refs['input'] && this.$refs['input'].focus();
                    });
                })
            },
            openProfile() {
                if (this.user) {
                    GlobalEventBus.$emit('showChatByUserId', this.user.userId);
                }
            },
            onFetchingStarted() {
                this.message = '';
            },
            onFetchingEnded() {
                if (this.$refs['input']) {
                    this.$refs['input'] && this.$refs['input'].focus();
                }
            }
        },

    });
</script>

<style scoped>

</style>
